import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiButton from '@mui/material/Button';
import { Plus } from 'lucide-react';
import { toast } from '../../../../ui/toast';
import { DIALOG_TYPE } from '../../../../constants';
import {
  ContextMenu,
  DataTable,
  Dialog,
  FilterSearch,
  MainLayout,
} from '../../../../ui';
import {
  addPartnerEmployee,
  deletePartnerEmployee,
  editPartnerEmployee,
  getPartnerEmployees,
  getPartnerList,
} from '../../../../utils/api_service/partner_api';
import { Permission } from '../../../../utils/helpers/permissions';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import { fetchUserGroups } from '../../../../utils/redux_store/features/user_group_reducer';
import { fetchUserRoles } from '../../../../utils/redux_store/features/user_role_reducer';
import { RootState, store } from '../../../../utils/redux_store/store';
import {
  PartnerEntity,
  PartnerUser,
} from '../../../../utils/types/admin_types';
import { FormattedSelectOptions } from '../../../../utils/types/analytics/analytics_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { AddPartnerEmployeeModal } from './Modals/AddPartnerEmployeeModal';
import { EditPartnerEmployeeModal } from './Modals/EditPartnerEmployee';
import { getColumns, getMenuItemsForMenu } from './Functions';

export const PartnerEmployeeManagement: React.FC = () => {
  const [partnerEmployees, setPartnerEmployees] = useState<PartnerUser[]>([]);
  const [partnerList, setPartnerList] = useState<PartnerEntity[]>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const userGroup = useSelector(
    (state: RootState) => state.userGroups.userGroupList,
  );
  const userRole = useSelector(
    (state: RootState) => state.userRoles.userRoleList,
  );

  const [addEmployeeModalVisible, setAddEmployeeModalVisible] =
    useState<boolean>(false);
  const [editEmployeeModalVisible, setEditEmployeeModalVisible] =
    useState<boolean>(false);
  const [editedPartnerEmployee, setEditedPartnerEmployee] =
    useState<PartnerUser | null>(null);

  // Context menu & delete state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<PartnerUser | null>(null);
  const [deleteConfirmState, setDeleteConfirmState] = useState<boolean>(false);
  const [employeeToDelete, setEmployeeToDelete] =
    useState<PartnerUser | null>(null);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPartnerEmployees({
        page: pageNumber,
        recordsPerPage: 10,
        query: searchTerm.trim() || undefined,
      });
      if (response.ok) {
        const data = await response.json();
        setPartnerEmployees(data.partner || []);
        setTotalPages(data.pages || 1);
        setTotalRecords(data.total || data.partner?.length || 0);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error fetching partners: ' + error, {
        position: 'bottom-center',
      });
    } finally {
      setLoading(false);
    }
  }, [pageNumber, searchTerm]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  useEffect(() => {
    store.dispatch(fetchUserGroups());
    store.dispatch(fetchUserRoles());
  }, []);

  useEffect(() => {
    const fetchPartnersList = async () => {
      try {
        const response = await getPartnerList({ subEntities: true });
        if (response.ok) {
          const data = await response.json();
          setPartnerList(data);
        } else {
          const errorData = await response.json();
          toast.error(errorData.error, { position: 'bottom-center' });
        }
      } catch (error) {
        toast.error('Error fetching partners: ' + error, {
          position: 'bottom-center',
        });
      }
    };

    fetchPartnersList();
  }, []);

  const handleOpenMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: PartnerUser,
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openEditModal = (partnerEmployee: PartnerUser) => {
    setEditedPartnerEmployee(partnerEmployee);
    setEditEmployeeModalVisible(true);
  };

  const openDeleteDialog = (partnerEmployee: PartnerUser) => {
    setEmployeeToDelete(partnerEmployee);
    setDeleteConfirmState(true);
  };

  const createPartnerEmployee = async (
    email: string,
    firstName: string,
    lastName: string,
    partnerEntityId: FormattedSelectOptions,
    jobTitle: string,
  ) => {
    try {
      const response = await addPartnerEmployee(
        email,
        firstName,
        lastName,
        partnerEntityId,
        jobTitle,
      );
      if (response.ok) {
        const data = await response.json();
        setPartnerEmployees((prev) => [...prev, data]);
        setAddEmployeeModalVisible(false);
        toast.success('Partner employee added successfully.', {
          position: 'bottom-center',
        });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error Adding Partner Employee: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const updatePartnerEmployee = async (
    id: string,
    partnerEntityId: FormattedSelectOptions,
    jobTitle: string,
    roles: SelectOptions[],
    groups: SelectOptions[],
  ) => {
    try {
      const response = await editPartnerEmployee(
        id,
        partnerEntityId,
        jobTitle,
        roles.map((option) => option.value),
        groups.map((option) => option.value),
      );
      if (response.ok) {
        const data = await response.json();
        setPartnerEmployees((prev) =>
          prev.map((emp) => (emp.id === data.id ? { ...emp, ...data } : emp)),
        );
        setEditEmployeeModalVisible(false);
        toast.success('Partner employee updated successfully.', {
          position: 'bottom-center',
        });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error Editing Partner Employee: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const removePartnerEmployee = async () => {
    if (!employeeToDelete) return;
    try {
      const response = await deletePartnerEmployee(employeeToDelete.id);
      if (response.ok) {
        setPartnerEmployees((prev) =>
          prev.filter((emp) => emp.id !== employeeToDelete.id),
        );
        toast.success('Partner employee deleted.', {
          position: 'bottom-center',
        });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error Deleting Partner Employee: ' + error, {
        position: 'bottom-center',
      });
    } finally {
      setDeleteConfirmState(false);
      setEmployeeToDelete(null);
    }
  };

  const contextMenuItems = getMenuItemsForMenu({
    onEdit: (row: PartnerUser) => openEditModal(row),
    onDelete: (row: PartnerUser) => openDeleteDialog(row),
  });

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <MainLayout>
        {/* Header Toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 400 } }}>
            <FilterSearch
              onSearch={(term) => {
                setSearchTerm(term);
                setPageNumber(1);
              }}
              searchText="Search partner employees..."
            />
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <PermissionGate requiredPermissions={[Permission.PARTNER_CREATE]}>
              <MuiButton
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setAddEmployeeModalVisible(true)}
                sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  bgcolor: 'primary.main',
                  color: '#ffffff',
                  px: 2.25,
                  py: 0.85,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
              >
                Add Partner Employee
              </MuiButton>
            </PermissionGate>
          </Stack>
        </Box>

        {/* Data Table */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: '6px',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
            width: '100%',
            minHeight: 360,
          }}
        >
          <DataTable<PartnerUser>
            isLoading={loading}
            columns={getColumns({
              handleOpenMenu,
              handleCloseMenu,
              anchorEl,
            })}
            data={partnerEmployees}
            rowsPerPage={10}
            onPageChange={(page) => setPageNumber(page)}
            currentPage={pageNumber}
            totalPages={totalPages}
            totalRecords={totalRecords}
          />
        </Paper>
      </MainLayout>

      {/* Delete Confirmation Dialog */}
      <Dialog
        title="Are you sure you want to delete this partner employee?"
        handleApply={removePartnerEmployee}
        confirmButtonLable="Yes, Delete"
        isOpen={deleteConfirmState}
        onClose={() => setDeleteConfirmState(false)}
        cancelButtonLable="Cancel"
        dialogType={DIALOG_TYPE.ERROR}
        justify="end"
      >
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          This action cannot be undone. All access for this partner employee will be revoked.
        </Typography>
      </Dialog>

      {/* Modals */}
      <AddPartnerEmployeeModal
        open={addEmployeeModalVisible}
        onClose={() => setAddEmployeeModalVisible(false)}
        partnerEntityList={partnerList || []}
        addPartnerEmployee={createPartnerEmployee}
      />

      {editedPartnerEmployee && (
        <EditPartnerEmployeeModal
          open={editEmployeeModalVisible}
          onClose={() => setEditEmployeeModalVisible(false)}
          partnerEntityList={partnerList || []}
          partnerEmployee={editedPartnerEmployee}
          userGroupList={userGroup}
          userRoleList={userRole}
          editPartnerEmployee={updatePartnerEmployee}
        />
      )}

      {/* Context Menu */}
      {anchorEl && selectedRow && (
        <ContextMenu<PartnerUser>
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          row={selectedRow}
          menuItems={contextMenuItems}
        />
      )}
    </Box>
  );
};
